import { getMachineStructureNodeset } from "@src/cloud-connect/dependency-injection-container/get-machine-structure-nodeset";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Tile } from "@src/cloud-connect/blueprint/dashboards/entities/tile";
import { findFirstTileTextNodeById } from "./find-first-tile-text-node-by-id";
import { getTimeseriesRawDataForDashboard } from "@src/cloud-connect/use-cases/get-timeseries-raw-data-for-dashboard";
import { fetchTimeseriesAggregatedDataAndBuildObjectForChart } from "./fetch-timeseries-aggregated-data-and-build-object-for-chart";
import { fetchTimeseriesRawDataAndBuildObjectForChart } from "./fetch-timeseries-raw-data-and-build-object-for-chart";
import { RootState } from "@src/reducers/store";
import { processNodeInformation } from "./process-node-information";

export const useDashboardPanelHook = (tile: Tile, groupId, inView: boolean) => {
  const [graphOptionsObject, setGraphOptionsObject] = useState(undefined);
  const [valuesForTextTile, setValuesForTextTile] = useState(undefined);
  const [nodeInfoList, setNodeInfoList] = useState<
    { nodeName: string; tsid: string; nodeId: string }[]
  >([]);
  const isInitialRender = useRef(true);

  const globalLookForLastTimeStampBeforeFetchingData = useSelector(
    (state: RootState) =>
      state.yourSlice.globalLookForLastTimeStampBeforeFetchingData,
  );

  const shouldItFetchData = useRef(true);

  const [
    lookForLastTimeStampBeforeFetchingData,
    setLookForLastTimeStampBeforeFetchingData,
  ] = useState(false);

  const onToggle = () => {
    setLookForLastTimeStampBeforeFetchingData(
      !lookForLastTimeStampBeforeFetchingData,
    );
    setValuesForTextTile(undefined);
    setGraphOptionsObject(undefined);
    shouldItFetchData.current = true;
  };

  const setAllToTrueOrFalse = () => {
    if (
      lookForLastTimeStampBeforeFetchingData !==
      globalLookForLastTimeStampBeforeFetchingData.value
    ) {
      setLookForLastTimeStampBeforeFetchingData(
        globalLookForLastTimeStampBeforeFetchingData.value,
      );
      setValuesForTextTile(undefined);
      setGraphOptionsObject(undefined);
    }
  };

  const setNoDataAvailableForTextTileWhenNoData = () => {
    setValuesForTextTile({
      nodeName: tile.getNodes()[tile.findFirstTextTileIndex()].getName(),
      value: "No data available",
      unit: "",
    });
  };

  const fetchTimeseriesDataForMultipleNodesAndRetrieveLastValueForTextTile =
    useCallback(
      async (signal?: AbortSignal) => {
        const response = await getTimeseriesRawDataForDashboard(
          groupId,
          tile,
          lookForLastTimeStampBeforeFetchingData,
          signal,
        );
        if (signal?.aborted) return;

        if (response !== null) {
          const nodeId = tile.getNodes()[tile.findFirstTextTileIndex()].getId();

          const timeseriesDataOfFirstTextNodeInTile = findFirstTileTextNodeById(
            response,
            nodeId,
          );
          processNodeInformation({
            response,
            tile,
            setState: setNodeInfoList,
          });
          if (timeseriesDataOfFirstTextNodeInTile) {
            /// this looks for the first node in the response that matches the id of the first text node in the tile (could be changed in the future if we want to show more than one information in the text tile)
            const lastValueToBeShownInTextTile =
              timeseriesDataOfFirstTextNodeInTile?.body?.values[
                timeseriesDataOfFirstTextNodeInTile.body.values.length - 1
              ].value;

            const unit =
              getMachineStructureNodeset().getNodeById(nodeId)?.attributes?.unit
                ?.display_name;

            setValuesForTextTile({
              nodeName: tile
                .getNodes()
                [tile.findFirstTextTileIndex()].getName(),
              value: lastValueToBeShownInTextTile,
              unit: unit,
            });
          } else {
            setNoDataAvailableForTextTileWhenNoData();
          }
        } else {
          setNoDataAvailableForTextTileWhenNoData();
        }
      },
      [lookForLastTimeStampBeforeFetchingData],
    );

  const fetchTimeseriesDataAndBuildObjectForChart = useCallback(
    (signal?: AbortSignal) => {
      tile.getNodes().length !== 1 || tile.getUseAggregatedForSingleNode()
        ? fetchTimeseriesAggregatedDataAndBuildObjectForChart(
            groupId,
            tile,
            lookForLastTimeStampBeforeFetchingData,
            setGraphOptionsObject,
            setNodeInfoList,
            signal,
          )
        : fetchTimeseriesRawDataAndBuildObjectForChart(
            groupId,
            tile,
            lookForLastTimeStampBeforeFetchingData,
            setGraphOptionsObject,
            setNodeInfoList,
            signal,
          );
    },
    [lookForLastTimeStampBeforeFetchingData],
  );

  useEffect(() => {
    if (!inView) return;
    if (!shouldItFetchData.current) return;

    const controller = new AbortController();
    const signal = controller.signal;

    tile.isItATextTile()
      ? fetchTimeseriesDataForMultipleNodesAndRetrieveLastValueForTextTile(
          signal,
        )
      : fetchTimeseriesDataAndBuildObjectForChart(signal);

    shouldItFetchData.current = false;

    return () => {
      controller.abort();
    };
  }, [
    inView,
    fetchTimeseriesDataAndBuildObjectForChart,
    fetchTimeseriesDataForMultipleNodesAndRetrieveLastValueForTextTile,
    tile,
  ]);

  useEffect(() => {
    shouldItFetchData.current = true;
  }, [globalLookForLastTimeStampBeforeFetchingData.updatedAt]);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false; // Skip the first render
      return;
    }

    setAllToTrueOrFalse();
  }, [globalLookForLastTimeStampBeforeFetchingData.updatedAt]);

  return {
    lookForLastTimeStampBeforeFetchingData,
    graphOptionsObject,
    valuesForTextTile,
    nodeInfoList,
    onToggle,
  };
};
